import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { salaryAPI } from '../../api/salaries';
import { driverAPI } from '../../api/drivers';
import { Driver } from '../../types/entities';
import { Search, Calculator } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { DateRangeQuickFilter } from '../../components/UI/DateRangeQuickFilter';
import {
  SearchableCombobox,
  createDriverOptions,
} from '../../components/UI/SearchableCombobox';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

interface SalaryResult {
  driverId: number;
  driverName: string;
  startDate: string;
  endDate: string;
  tripCount: number;
  totalDistanceKm: number;
  calculatedSalary: number;
  trips: Array<{
    tripId: number;
    date: string;
    vehicle: string;
    distanceKm?: number;
    driverWage: number;
  }>;
}

interface MonthlySalary {
  driverId: number;
  driverName: string;
  month: number;
  year: number;
  tripCount: number;
  totalDistanceKm: number;
  totalSalary: number;
}

export const SalaryCalculation: React.FC = () => {
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'individual' | 'monthly'>('monthly');

  // Individual calculation
  const [selectedDriver, setSelectedDriver] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [individualResult, setIndividualResult] = useState<SalaryResult | null>(null);

  // Monthly calculation
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthlyResults, setMonthlyResults] = useState<MonthlySalary[]>([]);

  const driverOptions = useMemo(() => createDriverOptions(drivers), [drivers]);

  useEffect(() => {
    loadDrivers();
  }, []);

  const loadDrivers = async () => {
    try {
      const data = await driverAPI.getAll();
      setDrivers(data.filter((d) => d.status === 'ACTIVE'));
    } catch (error) {
      toast.error(t('drivers.loadError'));
    }
  };

  const handleIndividualCalculate = async () => {
    if (!selectedDriver || !startDate || !endDate) {
      toast.error(t('salaries.selectDriverAndDates'));
      return;
    }

    setLoading(true);
    try {
      const result = await salaryAPI.calculate({
        driverId: selectedDriver,
        startDate,
        endDate,
      });
      setIndividualResult(result as SalaryResult);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('salaries.calculateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleMonthlyCalculate = async () => {
    setLoading(true);
    try {
      const results = await salaryAPI.getMonthlySalaries(month, year);
      setMonthlyResults(results);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('salaries.calculateError'));
    } finally {
      setLoading(false);
    }
  };

  const totalMonthlySalary = Array.isArray(monthlyResults)
    ? monthlyResults.reduce((sum, r) => sum + r.totalSalary, 0)
    : 0;

  const trips = individualResult?.trips || [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('salaries.title')}</h1>

      <div className="card">
        {/* Tabs */}
        <div className="flex border-b mb-4">
          <button
            onClick={() => setActiveTab('monthly')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'monthly'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500'
            }`}
          >
            {t('salaries.monthlySalary')}
          </button>
          <button
            onClick={() => setActiveTab('individual')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'individual'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500'
            }`}
          >
            {t('salaries.individualCalculation')}
          </button>
        </div>

        {/* Monthly Calculation */}
        {activeTab === 'monthly' && (
          <div className="space-y-4">
            <div className="flex items-end space-x-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('salaries.month')}
                </label>
                <select
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  className="input-field"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {t(`salaries.months.${i + 1}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('salaries.year')}
                </label>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  className="input-field"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const y = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </select>
              </div>
              <button
                onClick={handleMonthlyCalculate}
                disabled={loading}
                className="btn-primary flex items-center"
              >
                <Calculator className="w-4 h-4 mr-2" />
                {loading ? t('common.loading') : t('salaries.calculate')}
              </button>
            </div>

            {Array.isArray(monthlyResults) && monthlyResults.length > 0 && (
              <>
                <div className="bg-primary-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">{t('salaries.totalSalary')}:</span>
                    <span className="text-2xl font-bold text-primary-600">
                      {formatCurrency(totalMonthlySalary)} đ
                    </span>
                  </div>
                </div>

                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-left">
                      <th className="pb-3 table-header">{t('salaries.driver')}</th>
                      <th className="pb-3 table-header text-right">{t('reports.tripCount')}</th>
                      <th className="pb-3 table-header text-right">{t('salaries.totalDistance')}</th>
                      <th className="pb-3 table-header text-right">{t('salaries.salary')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyResults.map((result) => (
                      <tr key={result.driverId} className="border-b last:border-0">
                        <td className="py-3 font-medium">{result.driverName}</td>
                        <td className="py-3 text-right">{result.tripCount}</td>
                        <td className="py-3 text-right">
                          {result.totalDistanceKm ? `${result.totalDistanceKm.toFixed(1)} km` : '-'}
                        </td>
                        <td className="py-3 text-right font-semibold text-green-600">
                          {formatCurrency(result.totalSalary)} đ
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {(!Array.isArray(monthlyResults) || monthlyResults.length === 0) && !loading && (
              <div className="text-center text-gray-500 py-8">
                {t('salaries.selectMonthToCalculate')}
              </div>
            )}
          </div>
        )}

        {/* Individual Calculation */}
        {activeTab === 'individual' && (
          <div className="space-y-4">
            <div className="flex items-end space-x-4">
              <DateRangeQuickFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
              />
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">
                  {t('salaries.driver')} <span className="text-red-500">*</span>
                </label>
                <SearchableCombobox
                  options={driverOptions}
                  value={selectedDriver}
                  onChange={(val) => setSelectedDriver(val as number | null)}
                  placeholder={t('trips.selectDriver')}
                />
              </div>
              <button
                onClick={handleIndividualCalculate}
                disabled={loading}
                className="btn-primary flex items-center"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? t('common.loading') : t('salaries.calculate')}
              </button>
            </div>

            {individualResult && (
              <div className="space-y-4 mt-6">
                {/* Summary Card */}
                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-600">{t('salaries.driver')}</p>
                      <p className="text-lg font-semibold">{individualResult.driverName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('reports.tripCount')}</p>
                      <p className="text-lg font-semibold">{individualResult.tripCount}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('salaries.calculatedSalary')}</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(individualResult.calculatedSalary)} đ
                      </p>
                    </div>
                  </div>
                </div>

                {/* Trip Details */}
                {trips.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('salaries.tripDetails')}</h3>
                    <table className="w-full text-sm">
                      <thead className="border-b">
                        <tr className="text-left">
                          <th className="pb-2">{t('reports.tripId')}</th>
                          <th className="pb-2">{t('trips.date')}</th>
                          <th className="pb-2">{t('trips.vehicle')}</th>
                          <th className="pb-2 text-right">{t('trips.distance')}</th>
                          <th className="pb-2 text-right">{t('salaries.driverWagePerTrip')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trips.map((trip) => (
                          <tr key={trip.tripId} className="border-b last:border-0">
                            <td className="py-2">#{trip.tripId}</td>
                            <td className="py-2">
                              {format(new Date(trip.date), 'dd/MM/yyyy')}
                            </td>
                            <td className="py-2">{trip.vehicle || '-'}</td>
                            <td className="py-2 text-right">
                              {trip.distanceKm ? `${trip.distanceKm} km` : '-'}
                            </td>
                            <td className="py-2 text-right font-medium">
                              {formatCurrency(trip.driverWage)} đ
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {!individualResult && !loading && (
              <div className="text-center text-gray-500 py-8">
                {t('salaries.selectDriverAndDatesToCalculate')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
